// Business Foundation Guide v2 — mission FRAMEWORK.
// This defines the fixed 6-section structure every mission uses.
// Content is SEEDED from the live guide (data/playbook.js) as a starting point
// only — it is NOT rewritten here. Each mission will be rebuilt one at a time.
// The live guide (playbook.js / PlaybookHome / StepDetail) is left untouched.

import { PLAYBOOK_STEPS, PLAYBOOK_PDF_URL } from "@/data/playbook";

export const GUIDE_V2_NAME = "RouteHaul Mentorship";

// Fixed mission section framework (order matters).
export const MISSION_SECTIONS = [
  { id: "what-it-is", n: 1, label: "What It Is" },
  { id: "why-it-matters", n: 2, label: "Why It Matters" },
  { id: "how-to-do-it", n: 3, label: "How To Do It" },
  { id: "what-to-do-next", n: 4, label: "What To Do Next" },
  { id: "routehaul-resources", n: 5, label: "RouteHaul Resources" },
  { id: "mission-complete", n: 6, label: "Mission Complete" },
];

export const PHASES = [
  { id: "Foundation", label: "Foundation" },
  { id: "Authority", label: "Authority" },
  { id: "Compliance", label: "Compliance" },
  { id: "Operations", label: "Operations" },
  { id: "Safety", label: "Safety" },
  { id: "Financials", label: "Financials" },
  { id: "Growth", label: "Growth" },
];

// Per-mission mentorship layer: taglines, time estimates, milestones and the
// interactive worksheet fields shown under section 5 (RouteHaul Resources).
const META = {
  1: {
    tagline: "Build your legal firewall before you spend a dollar on equipment.",
    duration: "45–60 min",
    milestone: "Your business exists as its own legal entity — your personal assets are protected.",
    worksheet: {
      intro: "Lock in your entity decision so nothing downstream gets blocked.",
      fields: [
        { id: "structure", label: "Which structure will you form?", type: "select", options: ["LLC (recommended)", "S-Corp", "C-Corp", "Sole Proprietor"] },
        { id: "state", label: "State of registration", type: "text", placeholder: "e.g. California" },
        { id: "businessName", label: "Business name (list 2 backups)", type: "text", placeholder: "Primary, backup 1, backup 2" },
        { id: "formationDate", label: "Target formation date", type: "date" },
        { id: "separation", label: "How will you keep business & personal money separate?", type: "textarea", placeholder: "Bank, card, bookkeeping plan…" },
      ],
    },
  },
  2: {
    tagline: "Give your carrier a real address and a federal tax ID.",
    duration: "30–45 min",
    milestone: "You have an EIN and a compliant commercial address on record.",
    worksheet: {
      intro: "Capture the identifiers every downstream filing will ask for.",
      fields: [
        { id: "address", label: "Commercial / registered business address", type: "text", placeholder: "Not a P.O. box for FMCSA" },
        { id: "ein", label: "EIN (once issued)", type: "text", placeholder: "XX-XXXXXXX" },
        { id: "bank", label: "Business bank account opened with", type: "text", placeholder: "Bank name" },
        { id: "stateTax", label: "State tax accounts you still need", type: "textarea", placeholder: "List any state registrations required" },
      ],
    },
  },
  3: {
    tagline: "Become a federally recognized motor carrier.",
    duration: "1–2 hrs",
    milestone: "Your USDOT and MC authority application is filed and tracking.",
    worksheet: {
      intro: "Record your numbers and the dates that matter during the protest window.",
      fields: [
        { id: "usdot", label: "USDOT number", type: "text", placeholder: "Once issued" },
        { id: "mc", label: "MC number", type: "text", placeholder: "Once issued" },
        { id: "filedDate", label: "Date application filed", type: "date" },
        { id: "protestEnds", label: "21-day protest period ends", type: "date" },
      ],
    },
  },
  4: {
    tagline: "Flip your authority from pending to active.",
    duration: "1 hr + quotes",
    milestone: "BOC-3 and insurance are on file — your authority can activate.",
    worksheet: {
      intro: "Don't buy the thinnest policy. Note your real coverage here.",
      fields: [
        { id: "processAgent", label: "Process agent (BOC-3 filer)", type: "text", placeholder: "Licensed agent" },
        { id: "insurer", label: "Insurance carrier", type: "text", placeholder: "Company / broker" },
        { id: "liability", label: "Liability coverage amount", type: "money", placeholder: "750000" },
        { id: "premium", label: "Monthly premium", type: "money", placeholder: "Estimated" },
      ],
    },
  },
  5: {
    tagline: "Two federal registrations that keep you road-legal.",
    duration: "45 min",
    milestone: "UCR paid and Clearinghouse profile active.",
    worksheet: {
      intro: "Track both so a roadside inspection never catches you out.",
      fields: [
        { id: "ucrDate", label: "UCR filed on", type: "date" },
        { id: "ctpa", label: "Consortium / TPA (C/TPA)", type: "text", placeholder: "Provider name" },
        { id: "clearinghouse", label: "Clearinghouse profile created?", type: "select", options: ["Not yet", "In progress", "Done"] },
      ],
    },
  },
  6: {
    tagline: "Automate your hours and your fuel tax.",
    duration: "1 hr + install",
    milestone: "ELD installed and IFTA account open.",
    worksheet: {
      intro: "Set up the infrastructure that keeps HOS and fuel tax clean.",
      fields: [
        { id: "eld", label: "ELD provider", type: "text", placeholder: "From FMCSA registered list" },
        { id: "iftaState", label: "IFTA base state", type: "text", placeholder: "Your home state" },
        { id: "iftaAccount", label: "IFTA account #", type: "text", placeholder: "Once issued" },
      ],
    },
  },
  7: {
    tagline: "The folder every auditor asks for first.",
    duration: "45 min",
    milestone: "A complete Driver Qualification file exists for every driver.",
    worksheet: {
      intro: "Fill the gaps before an auditor finds them.",
      fields: [
        { id: "medicalExpiry", label: "DOT medical card expires", type: "date" },
        { id: "mvrDate", label: "MVR pulled on", type: "date" },
        { id: "dqGaps", label: "What's still missing from your DQ file?", type: "textarea", placeholder: "Application, MVR, medical card, road test…" },
      ],
    },
  },
  8: {
    tagline: "Clear the test before anyone turns a wheel.",
    duration: "30 min",
    milestone: "Verified negative test + enrolled in a random pool.",
    worksheet: {
      intro: "This is a hard federal line — document it.",
      fields: [
        { id: "consortium", label: "Consortium (C/TPA)", type: "text", placeholder: "Provider name" },
        { id: "testDate", label: "Pre-employment test date", type: "date" },
        { id: "randomPool", label: "Enrolled in random pool?", type: "select", options: ["Not yet", "Enrolled"] },
      ],
    },
  },
  9: {
    tagline: "Turn your 12-month audit into a formality.",
    duration: "Ongoing",
    milestone: "Your records are organized and audit-ready.",
    worksheet: {
      intro: "Organized records now = a calm audit later.",
      fields: [
        { id: "system", label: "Where you store compliance records", type: "text", placeholder: "Folder, app, binder…" },
        { id: "auditDate", label: "Target / known audit date", type: "date" },
        { id: "gaps", label: "Records you still need to organize", type: "textarea", placeholder: "Maintenance, ELD logs, DQ, testing…" },
      ],
    },
  },
  10: {
    tagline: "Protect your cash flow before your first load.",
    duration: "1 hr",
    milestone: "Factoring set up and a broker vetting habit in place.",
    worksheet: {
      intro: "Profitable on paper isn't enough — plan for the money to actually arrive.",
      fields: [
        { id: "factoring", label: "Factoring company", type: "text", placeholder: "Provider name" },
        { id: "factorRate", label: "Factoring rate (%)", type: "text", placeholder: "e.g. 2.5%" },
        { id: "reserve", label: "Cash reserve target", type: "money", placeholder: "For slow weeks" },
        { id: "vetting", label: "Your broker vetting checklist", type: "textarea", placeholder: "Credit score, days-to-pay, authority status…" },
      ],
    },
  },
  11: {
    tagline: "Chase profit per mile, not just miles.",
    duration: "1 hr",
    milestone: "You've mapped your most profitable lanes.",
    worksheet: {
      intro: "Design your lanes on purpose instead of chasing cheap freight.",
      fields: [
        { id: "homeBase", label: "Home base / operating region", type: "text", placeholder: "City, state" },
        { id: "targetRpm", label: "Target rate per mile", type: "money", placeholder: "e.g. 2.25" },
        { id: "lanes", label: "Your top 3 target lanes", type: "textarea", placeholder: "Origin → destination x3" },
        { id: "deadheadPlan", label: "How you'll minimize deadhead", type: "textarea", placeholder: "Backhaul strategy…" },
      ],
    },
  },
  12: {
    tagline: "Systems that let you grow without breaking.",
    duration: "Ongoing",
    milestone: "You have repeatable systems ready to scale.",
    worksheet: {
      intro: "Write the systems down — that's what makes growth survivable.",
      fields: [
        { id: "dispatchSystem", label: "Your dispatch / back-office workflow", type: "textarea", placeholder: "How loads, billing and compliance flow" },
        { id: "csaTarget", label: "CSA score you'll protect", type: "text", placeholder: "Threshold to watch" },
        { id: "delegate", label: "What you'll delegate first", type: "textarea", placeholder: "The task that frees you to grow" },
      ],
    },
  },
};

// Shared download available on every mission for now (full printable guide).
const GUIDE_DOWNLOAD = { label: "The 12-Step Business Foundation Guide (PDF)", url: PLAYBOOK_PDF_URL, desc: "Full printable guide to keep on hand." };

// Build each mission into the fixed 6-section framework. Content is mapped
// (not rewritten) from the live guide until we rebuild missions individually.
export const MISSIONS = PLAYBOOK_STEPS.map((s) => {
  const m = META[s.n] || {};
  return {
    n: s.n,
    phase: s.tag,
    title: s.title,
    tagline: m.tagline || s.text,
    duration: m.duration || "~45 min",

    // 1 · What It Is
    whatItIs: s.whatItIs,

    // 2 · Why It Matters
    whyItMatters: s.why,
    mentorTip: s.note, // shown as a mentor callout inside Why It Matters

    // 3 · How To Do It
    howToIntro: s.overview,
    howToSteps: s.tasks,

    // 4 · What To Do Next  (single clear action — seeded from first step)
    whatToDoNext: s.tasks[0],

    // 5 · RouteHaul Resources (all in one place)
    resources: {
      worksheet: m.worksheet || { intro: "Capture your plan for this mission.", fields: [{ id: "notes", label: "Your notes & next actions", type: "textarea", placeholder: "Jot your plan here…" }] },
      templates: [],
      calculators: [],
      downloads: [GUIDE_DOWNLOAD],
      official: s.resources,
    },

    // 6 · Mission Complete (checklist reuses the mission's steps)
    checklist: s.tasks,
    milestone: m.milestone || "Mission complete.",
  };
});

export const TOTAL_MISSIONS = MISSIONS.length;
export const getMission = (n) => MISSIONS.find((mi) => mi.n === Number(n));

// Backwards-compatible aliases (dashboard still imports these names).
export const MODULES = MISSIONS;
export const TOTAL_MODULES = TOTAL_MISSIONS;
export const getModule = getMission;
